import {Button} from '@/components/ui/button/Button'
import {DialogDisclosure, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AgendaEventStatusDropdown} from '@/features/agenda/components/agenda-event-status-dropdown/AgendaEventStatusDropdown'
import {useAgendaFormDisabledOperation} from '@/features/agenda/hooks/useAgendaFormDisabledOperation'
import {useTranslation} from '@/translations/i18n'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {SubmitErrorHandler, useFormContext, useWatch} from 'react-hook-form'
import {CreateEventFormValidationSchema} from '../../formSchemas'
import {useMemo, useState} from 'react'
import {AgendaCreateEventConfirmationModal} from '../agenda-event-form/agenda-create-event-confirmation-modal/AgendaCreateEventConfirmationModal'
import {AgendaEventStatus} from '@/features/agenda/types'
import {validateAccessTypeCompatibility} from '@/features/agenda/utils'
import {Text} from '@/components/ui/text/Text'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'

export const CreateEventFormDialogFooter = ({disabledStatuses}: {disabledStatuses?: AgendaEventStatus[]}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()
    const areDisabledPatientRelatedFields = useAgendaFormDisabledOperation()
    const {getValues, handleSubmit, control} = useFormContext<CreateEventFormValidationSchema>()
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
    const consultants = useWatch({control, name: 'consultants'})
    const patients = useWatch({control, name: 'patients'})
    const status = useWatch({control, name: 'status'})
    const {palette} = useTheme()

    const isWarning = useMemo(() => {
        if (!patients || patients.length === 0) {
            return false
        }

        for (const patient of patients) {
            const projectType = patient.project?.type
            for (const [, consultant] of consultants.entries()) {
                const accessType = consultant.accessType?.accessType
                if (validateAccessTypeCompatibility(projectType, accessType, status)) {
                    return true
                }
            }
        }
        return false
    }, [consultants, patients, status])

    const confirmationDialog = useDialogStore({
        open: isConfirmationDialogOpen,
        setOpen: setIsConfirmationDialogOpen
    })

    const handleOpenConfirmationDialog = () => {
        confirmationDialog.show()
    }

    const handleCreateEventError: SubmitErrorHandler<CreateEventFormValidationSchema> = errors => {
        console.error(errors, {values: getValues()})
    }

    const onSubmit = handleSubmit(handleOpenConfirmationDialog, handleCreateEventError)

    return (
        <DialogFooter>
            {!areDisabledPatientRelatedFields && !isWarning ? (
                <AgendaEventStatusDropdown eventStatus={status || 'provisional'} disabledStatuses={disabledStatuses} />
            ) : patients.some(patient => patient.project) ? (
                <Text
                    size="textSm"
                    palette="danger"
                    shade="700"
                    weight="400"
                    style={{display: 'flex', alignItems: 'center', gap: 4}}
                >
                    <AlertCircleIcon style={{color: palette.danger[500]}} size={14} />
                    {t('agenda:incompatible_access_type_subtitle')}
                </Text>
            ) : (
                <div />
            )}
            <Flexbox gap={2}>
                <DialogDisclosure store={dialog} render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                {!isWarning && (
                    <Button variant="primaryNeutral" onClick={onSubmit} disabled={areDisabledPatientRelatedFields}>
                        {t('agenda:create_event')}
                    </Button>
                )}
            </Flexbox>
            {isConfirmationDialogOpen && (
                <AgendaCreateEventConfirmationModal
                    confirmationDialog={confirmationDialog}
                    defaultValues={getValues()}
                />
            )}
        </DialogFooter>
    )
}
